import React, { useCallback } from 'react';
import { useWatch } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { boolean, InferType, mixed, number, object, string } from 'yup';

import { ManagementType } from '@ndvr/portfolio-api';

import { RequestReason } from '@/cip/_openapi/portfolio-optimizer';
import { FormSheet, useDisableFormSheetSubmit } from '@/components/FormSheet';
import { titleCase } from 'utils';
import { useAccount } from 'views/portfolio/account-queries';
import { AccountSelectorTree, FormCheckbox, FormSection, FormSelect, requiredStringValidator } from 'widgets';
import { FormMoneyInput } from 'widgets/form/input/FormMoneyInput';
import { FormTextarea } from 'widgets/form/input/FormTextArea';

import { useSubmitInvestmentRequest } from './useSubmitInvestmentRequest';

// CASHFLOW is intentionally excluded — it is a legacy value kept for backward
// compatibility with existing DB rows and should not be selectable for new requests.
const SELECTABLE_REASONS = Object.values(RequestReason).filter((r) => r !== RequestReason.CASHFLOW);

const formSchema = object({
    accountGUID: string().required('Account selection is required'),
    accountGroupGUID: string().required(),
    householdGUID: string().required(),
    reason: requiredStringValidator.oneOf(SELECTABLE_REASONS),
    withdrawalAmount: mixed().when('reason', {
        is: RequestReason.WITHDRAWAL,
        then: () =>
            number()
                .typeError('Withdrawal amount is required')
                .required('Withdrawal amount is required')
                .min(1, 'Amount must be at least $1'),
        otherwise: () => mixed().notRequired(),
    }),
    description: string().when('reason', {
        is: RequestReason.WITHDRAWAL,
        then: () => string().notRequired(),
        otherwise: () => string().required('Description is required'),
    }),
    alreadyWired: boolean().default(false),
});

type FormType = InferType<typeof formSchema>;

export const CreateInvestmentRequestDrawer = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { submit, isPending } = useSubmitInvestmentRequest();

    const closeFn = useCallback(
        () => navigate({ pathname: '..', search: searchParams.toString() }, { replace: true }),
        [navigate, searchParams],
    );

    const defaultValues = {
        accountGUID: '',
        accountGroupGUID: '',
        householdGUID: '',
        reason: '' as RequestReason,
        withdrawalAmount: undefined,
        description: '',
        alreadyWired: false,
    };

    const handleSubmit = useCallback(
        async (formData: FormType, close: () => void) => {
            const outcome = await submit({
                accountGUID: formData.accountGUID,
                accountGroupGUID: formData.accountGroupGUID,
                householdGUID: formData.householdGUID,
                reason: formData.reason,
                description: formData.description,
                withdrawalAmount: formData.withdrawalAmount as number | undefined,
                alreadyWired: formData.alreadyWired,
            });
            if (outcome !== 'failure') close();
        },
        [submit],
    );

    return (
        <FormSheet
            title="Create New Investment Request"
            onClose={closeFn}
            formSchema={formSchema}
            onSubmit={handleSubmit}
            defaultValues={defaultValues}
            pending={isPending}
            submitLabel="Create"
        >
            <CreateInvestmentRequestFormContent />
        </FormSheet>
    );
};

const CreateInvestmentRequestFormContent = () => {
    const accountGUID = useWatch({ name: 'accountGUID' });
    const reason = useWatch({ name: 'reason' });
    const { data: account, isLoading: isAccountLoading } = useAccount(accountGUID);
    // Mirrors backend TradingStatus.from(...) sans REBALANCE_IN_PROGRESS.
    const isAutomaticTrading =
        !!account && account.managementType === ManagementType.AUTOMATIC && !account.tradingSuspendedAt;
    const tradingBlockedReason = !account
        ? null
        : account.managementType !== ManagementType.AUTOMATIC
          ? 'Investment requests can only be created for accounts with Automatic trading status.'
          : account.tradingSuspendedAt
            ? 'Trading is suspended for this account. Investment requests cannot be created until trading is re-enabled.'
            : null;
    const isWithdrawal = reason === RequestReason.WITHDRAWAL;
    useDisableFormSheetSubmit(isAccountLoading || !isAutomaticTrading);

    const reasonOptions = SELECTABLE_REASONS.map((r) => ({
        value: r,
        label: titleCase(r),
    }));

    return (
        <>
            <AccountSelectorTree
                accountField="accountGUID"
                portfolioField="accountGroupGUID"
                householdField="householdGUID"
            />
            {accountGUID && tradingBlockedReason && (
                <div className="rounded-md border-l-4 border-yellow-400 bg-yellow-50 p-4">
                    <p className="text-sm text-yellow-700">{tradingBlockedReason}</p>
                </div>
            )}
            {accountGUID && isAutomaticTrading && (
                <FormSection>
                    <FormSelect name="reason" label="Reason">
                        <option value=""></option>
                        {reasonOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </FormSelect>
                    {isWithdrawal && (
                        <>
                            <FormMoneyInput
                                name="withdrawalAmount"
                                label="Withdrawal Amount"
                                placeholder="Enter amount"
                            />
                            <FormCheckbox name="alreadyWired" label="Withdrawal already wired from cash/margin" />
                        </>
                    )}
                    <FormTextarea
                        name="description"
                        label="Description"
                        placeholder={
                            isWithdrawal
                                ? 'Optional — appended after "Urgent withdrawal - $X"'
                                : 'Provide a detailed description of this investment request...'
                        }
                        $height="150px"
                    />
                    {isWithdrawal && (
                        <p className="text-muted-foreground text-sm">
                            A withdrawal for the next business day will be created automatically.
                        </p>
                    )}
                </FormSection>
            )}
        </>
    );
};
